import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Check, Loader2, ArrowRight } from "lucide-react";
import { recipeApi, setSession } from "@/lib/recipeApi";

/**
 * Post-checkout flow:
 * 1. Poll payment status (webhook fallback covers slow webhooks)
 * 2. Once paid, show account-creation form (email + password)
 * 3. On submit, POST /auth/register with { email, password, session_id }
 * 4. Store JWT and redirect to /recipes/dashboard
 */
const RecipesSuccessPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");

  const [status, setStatus] = useState("checking");
  const [prefilledEmail, setPrefilledEmail] = useState("");
  const [accountExists, setAccountExists] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Poll payment status up to 20 tries (~40s)
  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await recipeApi.checkoutStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (data.email) {
            setPrefilledEmail(data.email);
            setEmail(data.email);
          }
          setAccountExists(!!data.account_exists);
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
      } catch {
        // ignore transient
      }
      tries += 1;
      if (tries >= 20) {
        setStatus("timeout");
      } else {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || password.length < 6) {
      toast.error("Enter a valid email and a password of at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await recipeApi.register(email.trim(), password, sessionId);
      setSession(data.token, data.user);
      toast.success("Account created — welcome!");
      navigate("/recipes/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white pt-28 pb-20 px-6" data-testid="recipes-success-page">
      <div className="max-w-md mx-auto">
        {status === "checking" && (
          <div className="text-center" data-testid="status-checking">
            <Loader2 size={40} className="mx-auto animate-spin text-[#D4AF37] mb-6" />
            <p className="text-white/70">Confirming your payment…</p>
          </div>
        )}

        {status === "failed" && (
          <div className="text-center" data-testid="status-failed">
            <p className="text-red-400 text-lg mb-4">Payment did not go through.</p>
            <Link
              to="/recipes"
              className="text-[#D4AF37] hover:underline text-sm tracking-wider uppercase"
            >
              Try again
            </Link>
          </div>
        )}

        {status === "timeout" && (
          <div className="text-center" data-testid="status-timeout">
            <p className="text-white/80 mb-4">
              We couldn&rsquo;t confirm your payment in time. If you were charged, please
              refresh this page or contact us.
            </p>
            <Button
              onClick={() => window.location.reload()}
              className="bg-[#D4AF37] text-black rounded-none"
            >
              Refresh
            </Button>
          </div>
        )}

        {status === "error" && (
          <div className="text-center" data-testid="status-error">
            <p className="text-white/80">Missing session id — return to <Link to="/recipes" className="text-[#D4AF37] underline">/recipes</Link>.</p>
          </div>
        )}

        {status === "paid" && !accountExists && (
          <>
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 border-2 border-[#D4AF37] rounded-full mb-6">
                <Check size={28} className="text-[#D4AF37]" />
              </div>
              <p className="text-[#D4AF37] text-xs tracking-[0.35em] uppercase mb-3">
                Payment Confirmed
              </p>
              <h1
                className="font-display text-4xl sm:text-5xl leading-[0.95] mb-3"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                CREATE YOUR <span className="text-[#D4AF37]">ACCOUNT</span>
              </h1>
              <p className="text-white/60 text-sm">
                Choose your login credentials so you can access your recipes from any
                device.
              </p>
            </div>

            <form
              onSubmit={submit}
              className="border border-[#1A1A1A] bg-black/60 p-6 sm:p-8 space-y-4"
              data-testid="register-form"
            >
              <div>
                <label className="text-[11px] tracking-[0.2em] uppercase text-white/60 mb-2 block font-semibold">
                  Email *
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="premium-input rounded"
                  autoComplete="email"
                  data-testid="register-email"
                />
                {prefilledEmail && (
                  <p className="text-white/40 text-[11px] italic mt-1">
                    Pre-filled from checkout — you can change it.
                  </p>
                )}
              </div>
              <div>
                <label className="text-[11px] tracking-[0.2em] uppercase text-white/60 mb-2 block font-semibold">
                  Password * <span className="text-white/40 normal-case tracking-normal ml-1">(min 6 chars)</span>
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="premium-input rounded"
                  autoComplete="new-password"
                  data-testid="register-password"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#D4AF37] text-black hover:bg-white rounded-none py-6 text-xs tracking-[0.25em] uppercase font-bold"
                data-testid="register-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    CREATING
                  </>
                ) : (
                  <>
                    Enter The Kitchen
                    <ArrowRight size={14} className="ml-2" />
                  </>
                )}
              </Button>
            </form>
          </>
        )}

        {status === "paid" && accountExists && (
          <div className="text-center" data-testid="account-exists">
            <p className="text-[#D4AF37] text-xs tracking-[0.35em] uppercase mb-3">
              Payment Confirmed
            </p>
            <h1
              className="font-display text-4xl sm:text-5xl mb-4"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              WELCOME <span className="text-[#D4AF37]">BACK</span>
            </h1>
            <p className="text-white/60 text-sm mb-8">
              An account for this payment already exists.
            </p>
            <Link
              to="/recipes/login"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black hover:bg-white px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold"
            >
              Log In
              <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipesSuccessPage;
